/**
 * Finds the applicable rate. A product-specific rate beats a category rate —
 * a vendor who quoted a special price for one item meant it to win.
 */
export function resolveVendorRate(vendor, productId, categoryIds, qty) {
    const eligible = (entry) => !entry.minQty || qty >= entry.minQty;
    const productRate = vendor.rateCard.find((r) => r.targetType === 'product' && r.targetId === productId && eligible(r));
    if (productRate)
        return productRate;
    // Among category rates, the cheapest applicable one.
    const categoryRates = vendor.rateCard.filter((r) => r.targetType === 'category' && categoryIds.includes(r.targetId) && eligible(r));
    if (categoryRates.length === 0)
        return null;
    return categoryRates.reduce((best, r) => (r.unitCostMinor < best.unitCostMinor ? r : best));
}
export function vendorCostFor(vendor, productId, categoryIds, qty) {
    const rate = resolveVendorRate(vendor, productId, categoryIds, qty);
    return rate ? rate.unitCostMinor * qty : null;
}
/** Weights. Cost matters, but never at the price of missing the promise. */
const WEIGHT = {
    cost: 45,
    speed: 25,
    reliability: 20,
    load: 10,
};
export function rankVendors(input) {
    const { item, categoryIds, vendors, daysAvailable, openJobsByVendor = {} } = input;
    const active = vendors.filter((v) => v.status === 'ACTIVE');
    const priced = active.map((vendor) => ({
        vendor,
        costMinor: vendorCostFor(vendor, item.productId, categoryIds, item.qty),
        capable: vendor.capabilities.some((c) => categoryIds.includes(c)),
    }));
    const costs = priced.map((p) => p.costMinor).filter((c) => c != null && c > 0);
    const cheapest = costs.length ? Math.min(...costs) : null;
    const dearest = costs.length ? Math.max(...costs) : null;
    const loads = Object.values(openJobsByVendor);
    const busiest = loads.length ? Math.max(...loads, 1) : 1;
    const scored = priced.map(({ vendor, costMinor, capable }) => {
        const reasons = [];
        const warnings = [];
        let score = 0;
        if (!capable) {
            warnings.push('Has not been set up to produce this category.');
        }
        // --- Cost -----------------------------------------------------
        if (costMinor == null) {
            warnings.push('No rate card entry for this product — cost would need agreeing.');
        }
        else if (cheapest != null && dearest != null) {
            // Everyone scores full marks when all vendors quote the same.
            const spread = dearest - cheapest;
            const costScore = spread === 0 ? 1 : 1 - (costMinor - cheapest) / spread;
            score += costScore * WEIGHT.cost;
            if (costMinor === cheapest)
                reasons.push('Lowest cost for this job.');
        }
        // --- Speed ----------------------------------------------------
        if (daysAvailable != null) {
            if (vendor.turnaroundDays <= daysAvailable) {
                // Comfortable margin scores higher than only just making it.
                const margin = (daysAvailable - vendor.turnaroundDays) / Math.max(daysAvailable, 1);
                score += (0.6 + 0.4 * Math.min(margin, 1)) * WEIGHT.speed;
                reasons.push(`Turnaround ${vendor.turnaroundDays} days, inside the ${daysAvailable} available.`);
            }
            else {
                warnings.push(`Needs ${vendor.turnaroundDays} days but only ${daysAvailable} remain before the promised date.`);
            }
        }
        else {
            score += 0.6 * WEIGHT.speed;
        }
        // --- Reliability ----------------------------------------------
        if (vendor.onTimeRate != null) {
            score += vendor.onTimeRate * WEIGHT.reliability;
            if (vendor.onTimeRate >= 0.95)
                reasons.push(`${Math.round(vendor.onTimeRate * 100)}% on time.`);
            if (vendor.onTimeRate < 0.8) {
                warnings.push(`Only ${Math.round(vendor.onTimeRate * 100)}% of their jobs have been on time.`);
            }
        }
        else {
            // No history is not a black mark, but it is not evidence either.
            score += 0.5 * WEIGHT.reliability;
            reasons.push('No completed jobs yet.');
        }
        // --- Load -----------------------------------------------------
        const openJobs = openJobsByVendor[vendor.id] ?? 0;
        score += (1 - Math.min(openJobs / busiest, 1)) * WEIGHT.load;
        if (openJobs === 0)
            reasons.push('No jobs in progress.');
        // A vendor who cannot make the product, or cannot make the date, must
        // never outrank one who can — so the penalty is applied last and is
        // large enough that no combination of cost and reliability overcomes it.
        if (!capable)
            score *= 0.25;
        const onTimeForPromise = daysAvailable == null || vendor.turnaroundDays <= daysAvailable;
        if (!onTimeForPromise)
            score *= 0.4;
        return {
            vendor,
            score: Math.round(Math.max(0, Math.min(100, score))),
            costMinor,
            capable,
            onTimeForPromise,
            reasons,
            warnings,
        };
    });
    return scored.sort((a, b) => b.score - a.score || a.vendor.name.localeCompare(b.vendor.name));
}
/** The single best suggestion, or null when nothing is usable. */
export function suggestVendor(input) {
    const ranked = rankVendors(input);
    const best = ranked[0];
    if (!best || !best.capable)
        return null;
    return best;
}
/** Margin on a line: what the customer paid minus what the vendor costs. */
export function marginMinor(customerPaidMinor, vendorCostMinor) {
    if (vendorCostMinor == null)
        return null;
    return customerPaidMinor - vendorCostMinor;
}
export function marginPercent(customerPaidMinor, vendorCostMinor) {
    if (vendorCostMinor == null || customerPaidMinor <= 0)
        return null;
    return Math.round(((customerPaidMinor - vendorCostMinor) / customerPaidMinor) * 100);
}
//# sourceMappingURL=vendorMatch.js.map